import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';

type ActivityType = 'poll_created' | 'poll_updated' | 'user_voted' | 'profile_updated' | 'poll_removed' | 'poll_closed' | 'poll_reopened';

export interface Activity {
  id?: string;
  type: ActivityType;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  userAvatarId?: number;
  pollId?: string;
  pollTitle?: string;
  isAnonymous: boolean;
  createdAt: Timestamp;
}

export interface CreateActivityData {
  type: ActivityType;
  userId: string;
  userName: string;
  userPhotoURL?: string;
  userAvatarId?: number;
  pollId?: string;
  pollTitle?: string;
  isAnonymous: boolean;
}

/**
 * Creates a new activity record in Firestore
 */
export async function createActivity(activityData: CreateActivityData): Promise<string> {
  try {
    // Clean the activity data to remove undefined values
    const cleanedData: Record<string, unknown> = {
      type: activityData.type,
      userId: activityData.userId,
      userName: activityData.userName,
      isAnonymous: activityData.isAnonymous,
      createdAt: serverTimestamp() as Timestamp,
    };

    // Add poll-related fields if they exist
    if (activityData.pollId) {
      cleanedData.pollId = activityData.pollId;
    }
    if (activityData.pollTitle) {
      cleanedData.pollTitle = activityData.pollTitle;
    }

    // Only add optional fields if they have values
    if (activityData.userPhotoURL) {
      cleanedData.userPhotoURL = activityData.userPhotoURL;
    }
    if (activityData.userAvatarId !== undefined && activityData.userAvatarId !== null) {
      cleanedData.userAvatarId = activityData.userAvatarId;
    }

    const docRef = await addDoc(collection(db, 'activities'), cleanedData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating activity:', error);
    throw new Error('Failed to create activity');
  }
}

/**
 * Creates a profile update activity
 */
export async function createProfileUpdateActivity(
  userId: string,
  userName: string,
  isAnonymous: boolean,
  userPhotoURL?: string,
  userAvatarId?: number
): Promise<void> {
  try {
    await createActivity({
      type: 'profile_updated',
      userId,
      userName,
      userPhotoURL,
      userAvatarId,
      isAnonymous,
    });
  } catch (error) {
    console.error('Error creating profile update activity:', error);
    // Don't throw error for activity logging failure
  }
}

/**
 * Creates a poll removal activity
 */
export async function createPollRemovalActivity(
  userId: string,
  userName: string,
  pollId: string,
  pollTitle: string,
  isAnonymous: boolean = false,
  userPhotoURL?: string,
  userAvatarId?: number
): Promise<void> {
  try {
    await createActivity({
      type: 'poll_removed',
      userId,
      userName,
      userPhotoURL,
      userAvatarId,
      pollId,
      pollTitle,
      isAnonymous,
    });
  } catch (error) {
    console.error('Error creating poll removal activity:', error);
    // Don't throw error for activity logging failure
  }
}

/**
 * Creates a poll closure activity
 */
export async function createPollClosureActivity(
  userId: string,
  userName: string,
  pollId: string,
  pollTitle: string,
  isAnonymous: boolean = false,
  userPhotoURL?: string,
  userAvatarId?: number
): Promise<void> {
  try {
    await createActivity({
      type: 'poll_closed',
      userId,
      userName,
      userPhotoURL,
      userAvatarId,
      pollId,
      pollTitle,
      isAnonymous,
    });
  } catch (error) {
    console.error('Error creating poll closure activity:', error);
    // Don't throw error for activity logging failure
  }
}

/**
 * Creates a poll reopening activity
 */
export async function createPollReopeningActivity(
  userId: string,
  userName: string,
  pollId: string,
  pollTitle: string,
  isAnonymous: boolean = false,
  userPhotoURL?: string,
  userAvatarId?: number
): Promise<void> {
  try {
    await createActivity({
      type: 'poll_reopened',
      userId,
      userName,
      userPhotoURL,
      userAvatarId,
      pollId,
      pollTitle,
      isAnonymous,
    });
  } catch (error) {
    console.error('Error creating poll reopening activity:', error);
    // Don't throw error for activity logging failure
  }
}

/**
 * Gets activities related to polls created by a specific user (including votes by others)
 */
export async function getPollActivities(pollIds: string[], limitCount: number = 20): Promise<Activity[]> {
  try {
    if (pollIds.length === 0) {
      return [];
    }

    const q = query(
      collection(db, 'activities'),
      where('pollId', 'in', pollIds.slice(0, 10)), // Firestore 'in' limit is 10
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const activities: Activity[] = [];

    querySnapshot.forEach((doc) => {
      activities.push({
        id: doc.id,
        ...doc.data(),
      } as Activity);
    });

    return activities;
  } catch (error) {
    console.error('Error getting poll activities:', error);
    throw new Error('Failed to get poll activities');
  }
}

/**
 * Sets up a real-time listener for activities related to user's polls and profile
 */
export function subscribeToActivities(
  pollIds: string[],
  callback: (activities: Activity[]) => void,
  limitCount: number = 20,
  userId?: string
): () => void {
  try {
    // We need to combine two queries: poll activities and user activities
    // Since Firestore doesn't support OR queries easily, we'll use two separate subscriptions

    let pollActivities: Activity[] = [];
    let userActivities: Activity[] = [];

    const combineAndCallback = () => {
      // Combine and sort activities by creation date, removing duplicates
      const allActivities = [...pollActivities, ...userActivities];
      const uniqueActivities = allActivities.filter((activity, index, self) =>
        index === self.findIndex(a => a.id === activity.id)
      );

      const sortedActivities = uniqueActivities
        .filter(activity => activity.createdAt != null) // Filter out activities with null/undefined timestamps
        .sort((a, b) => {
          // Additional safety check
          if (!a.createdAt || !b.createdAt) return 0;
          return b.createdAt.toMillis() - a.createdAt.toMillis();
        })
        .slice(0, limitCount);

      callback(sortedActivities);
    };

    const unsubscribeFunctions: (() => void)[] = [];

    // Subscribe to poll-related activities if we have polls
    if (pollIds.length > 0) {
      const pollQuery = query(
        collection(db, 'activities'),
        where('pollId', 'in', pollIds.slice(0, 10)), // Firestore 'in' limit is 10
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const unsubscribePoll = onSnapshot(pollQuery, (querySnapshot) => {
        pollActivities = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Only add activities with valid timestamps
          if (data.createdAt && data.createdAt.toMillis) {
            pollActivities.push({
              id: doc.id,
              ...data,
            } as Activity);
          }
        });
        combineAndCallback();
      }, (error) => {
        console.error('Error in poll activities subscription:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for poll activities - user may be anonymous or lack permissions');
        }
        pollActivities = [];
        combineAndCallback();
      });

      unsubscribeFunctions.push(unsubscribePoll);
    }

    // Subscribe to all activities by this user (including deleted polls and profile updates)
    if (userId) {
      const userQuery = query(
        collection(db, 'activities'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const unsubscribeUser = onSnapshot(userQuery, (querySnapshot) => {
        userActivities = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Only add activities with valid timestamps
          if (data.createdAt && data.createdAt.toMillis) {
            userActivities.push({
              id: doc.id,
              ...data,
            } as Activity);
          }
        });
        combineAndCallback();
      }, (error) => {
        console.error('Error in user activities subscription:', error);
        if (error.code === 'permission-denied') {
          console.log('Permission denied for user activities - user may be anonymous or lack permissions');
        }
        userActivities = [];
        combineAndCallback();
      });

      unsubscribeFunctions.push(unsubscribeUser);
    }

    // Return a function that unsubscribes from all subscriptions
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };

  } catch (error) {
    console.error('Error setting up activities subscription:', error);
    callback([]);
    return () => {};
  }
}

/**
 * Helper function to format activity timestamp for display
 */
export function formatActivityTime(timestamp: Timestamp): string {
  if (!timestamp || !timestamp.toDate) {
    return 'dashboard.activity.justNow'; // Fallback for invalid timestamps
  }

  const now = new Date();
  const activityTime = timestamp.toDate();
  const diffMs = now.getTime() - activityTime.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) {
    return 'dashboard.activity.justNow';
  } else if (diffMinutes < 60) {
    return 'dashboard.activity.minutesAgo';
  } else if (diffHours < 24) {
    return 'dashboard.activity.hoursAgo';
  } else if (diffDays < 7) {
    return 'dashboard.activity.daysAgo';
  } else {
    return 'dashboard.activity.weeksAgo';
  }
}

/**
 * Helper function to get the count for time formatting
 */
export function getActivityTimeCount(timestamp: Timestamp): number {
  if (!timestamp || !timestamp.toDate) {
    return 0; // Fallback for invalid timestamps
  }

  const now = new Date();
  const activityTime = timestamp.toDate();
  const diffMs = now.getTime() - activityTime.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));

  if (diffMinutes < 60) {
    return diffMinutes;
  } else if (diffHours < 24) {
    return diffHours;
  } else if (diffDays < 7) {
    return diffDays;
  } else {
    return diffWeeks;
  }
}
